import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { route } from '@aurelia/router';
import template from './maintenance-view.html';
import { ConfirmDialog } from '../components/confirm-dialog';
import {
  deleteDuplicateMediaItems,
  formatFileSize,
  getMaintenanceInfo,
  getMediaPreviewUrl,
  getPersonMarkersExportUrl,
  listDuplicateGroups,
  streamBackfillEmbeddedMetadata,
  streamBackfillFaceEmbeddings,
  streamBackfillFaceSharpness,
  streamBackfillMediumPreviews,
  streamBackfillPersonMarkerPreviews,
  streamClearFilenameTitles,
  streamCleanupOrphanedFiles,
  streamExtractGeoLocations,
} from '../services/api.service';
import type { DuplicateGroup, MaintenanceInfo, MaintenanceProgressEvent } from '../services/api.types';
import { MediaType } from '../services/api.types';

@route({ path: 'maintenance', title: 'Maintenance' })
@customElement({ name: 'maintenance-view', template })
export class MaintenanceView {
  private readonly i18n: I18N = resolve(I18N);

  public info: MaintenanceInfo | null = null;
  public loadingInfo = false;
  public infoError = '';
  public cleanupRunning = false;
  public cleanupDeleteOrphanedDiskFiles = true;
  public cleanupDeleteMissingMediaItems = false;
  public cleanupProgress = 0;
  public cleanupTotal = 0;
  public cleanupMessage = '';

  public geoExtractionRunning = false;
  public geoExtractionProgress = 0;
  public geoExtractionTotal = 0;
  public geoExtractionMessage = '';

  public embeddedMetadataBackfillRunning = false;
  public embeddedMetadataBackfillProgress = 0;
  public embeddedMetadataBackfillTotal = 0;
  public embeddedMetadataBackfillMessage = '';

  public faceEmbeddingBackfillRunning = false;
  public faceEmbeddingBackfillProgress = 0;
  public faceEmbeddingBackfillTotal = 0;
  public faceEmbeddingBackfillMessage = '';
  public faceSharpnessBackfillRunning = false;
  public faceSharpnessBackfillProgress = 0;
  public faceSharpnessBackfillTotal = 0;
  public faceSharpnessBackfillMessage = '';
  public mediumPreviewBackfillRunning = false;
  public mediumPreviewBackfillProgress = 0;
  public mediumPreviewBackfillTotal = 0;
  public mediumPreviewBackfillMessage = '';
  public personMarkerPreviewBackfillRunning = false;
  public personMarkerPreviewBackfillProgress = 0;
  public personMarkerPreviewBackfillTotal = 0;
  public personMarkerPreviewBackfillMessage = '';
  public filenameTitlesRunning = false;
  public filenameTitlesProgress = 0;
  public filenameTitlesTotal = 0;
  public filenameTitlesMessage = '';

  // ── Duplicate resolution ────────────────────────────────────────────────────
  public duplicateGroups: DuplicateGroup[] = [];
  public duplicatesLoaded = false;
  public duplicatesLoading = false;
  public duplicatesError = '';
  public resolvingDuplicates = false;
  // fileHash -> id of the item to keep for that group; every other item in the group gets deleted.
  public keepSelection: Record<string, string> = {};
  public getMediaPreviewUrl = getMediaPreviewUrl;
  public readonly personMarkersExportUrl = getPersonMarkersExportUrl();

  public async loading(): Promise<void> {
    await this.refreshInfo();
  }

  public async refreshInfo(): Promise<void> {
    this.loadingInfo = true;
    this.infoError = '';
    try {
      this.info = await getMaintenanceInfo();
    } catch (error) {
      this.infoError = error instanceof Error ? error.message : String(error);
    } finally {
      this.loadingInfo = false;
    }
  }

  public get cleanupPercent(): number {
    if (this.cleanupTotal === 0) return 0;
    return Math.round((this.cleanupProgress / this.cleanupTotal) * 100);
  }

  public get geoExtractionPercent(): number {
    if (this.geoExtractionTotal === 0) return 0;
    return Math.round((this.geoExtractionProgress / this.geoExtractionTotal) * 100);
  }

  public get embeddedMetadataBackfillPercent(): number {
    if (this.embeddedMetadataBackfillTotal === 0) return 0;
    return Math.round((this.embeddedMetadataBackfillProgress / this.embeddedMetadataBackfillTotal) * 100);
  }

  public get faceEmbeddingBackfillPercent(): number {
    if (this.faceEmbeddingBackfillTotal === 0) return 0;
    return Math.round((this.faceEmbeddingBackfillProgress / this.faceEmbeddingBackfillTotal) * 100);
  }

  public get faceSharpnessBackfillPercent(): number {
    if (this.faceSharpnessBackfillTotal === 0) return 0;
    return Math.round((this.faceSharpnessBackfillProgress / this.faceSharpnessBackfillTotal) * 100);
  }

  public get mediumPreviewBackfillPercent(): number {
    if (this.mediumPreviewBackfillTotal === 0) return 0;
    return Math.round((this.mediumPreviewBackfillProgress / this.mediumPreviewBackfillTotal) * 100);
  }

  public get filenameTitlesPercent(): number {
    if (this.filenameTitlesTotal === 0) return 0;
    return Math.round((this.filenameTitlesProgress / this.filenameTitlesTotal) * 100);
  }

  public get personMarkerPreviewBackfillPercent(): number {
    if (this.personMarkerPreviewBackfillTotal === 0) return 0;
    return Math.round((this.personMarkerPreviewBackfillProgress / this.personMarkerPreviewBackfillTotal) * 100);
  }

  public formatBytes(value: number | null | undefined): string {
    return formatFileSize(value);
  }

  public isVideo(item: { mediaType: number }): boolean {
    return item.mediaType === MediaType.Video;
  }

  public collectionsLabel(item: { collections: { title: string }[] }): string {
    return item.collections.map(c => c.title).join(', ');
  }

  public get totalDuplicatesToDelete(): number {
    return this.duplicateGroups.reduce((sum, group) => sum + Math.max(0, group.items.length - 1), 0);
  }

  public get duplicatesLoadButtonLabel(): string {
    return this.duplicatesLoaded
      ? this.i18n.tr('maintenance.duplicates.refresh')
      : this.i18n.tr('maintenance.duplicates.load');
  }

  public personMarkerLabel(item: { personMarkerCount: number }): string {
    return item.personMarkerCount > 0
      ? String(item.personMarkerCount)
      : this.i18n.tr('maintenance.duplicates.noPersons');
  }

  public async loadDuplicates(): Promise<void> {
    this.duplicatesLoading = true;
    this.duplicatesError = '';
    try {
      this.duplicateGroups = await listDuplicateGroups();
      const nextSelection: Record<string, string> = {};
      for (const group of this.duplicateGroups) {
        // Default to keeping the earliest-uploaded copy (items already come back sorted that way).
        nextSelection[group.fileHash] = group.items[0]?.id ?? '';
      }
      this.keepSelection = nextSelection;
      this.duplicatesLoaded = true;
    } catch (error) {
      this.duplicatesError = error instanceof Error ? error.message : String(error);
    } finally {
      this.duplicatesLoading = false;
    }
  }

  public selectKeep(group: DuplicateGroup, itemId: string): void {
    this.keepSelection = { ...this.keepSelection, [group.fileHash]: itemId };
  }

  public async resolveDuplicates(): Promise<void> {
    if (this.resolvingDuplicates || this.totalDuplicatesToDelete === 0) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('maintenance.duplicates.confirm', { count: this.totalDuplicatesToDelete }),
      this.i18n.tr('maintenance.duplicates.title'),
    );
    if (!confirmed) return;

    this.resolvingDuplicates = true;
    try {
      const idsToDelete = this.duplicateGroups.flatMap(group => {
        const keepId = this.keepSelection[group.fileHash];
        return group.items.filter(item => item.id !== keepId).map(item => item.id);
      });

      const result = await deleteDuplicateMediaItems(idsToDelete);
      const message = result.errors.length > 0
        ? this.i18n.tr('maintenance.duplicates.resultWithErrors', {
            deleted: result.deletedCount,
            errors: result.errors.join('\n'),
          })
        : this.i18n.tr('maintenance.duplicates.result', { deleted: result.deletedCount });
      await ConfirmDialog.alert(message, this.i18n.tr('maintenance.duplicates.title'));

      await this.loadDuplicates();
      await this.refreshInfo();
    } catch (error) {
      await ConfirmDialog.alert(
        error instanceof Error ? error.message : String(error),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.resolvingDuplicates = false;
    }
  }

  public async runCleanup(): Promise<void> {
    if (this.cleanupRunning) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('maintenance.cleanup.confirm'),
      this.i18n.tr('maintenance.cleanup.title'),
    );
    if (!confirmed) return;

    this.cleanupRunning = true;
    this.cleanupProgress = 0;
    this.cleanupTotal = 0;
    this.cleanupMessage = '';
    let finalMessage = '';

    try {
      await streamCleanupOrphanedFiles(
        this.cleanupDeleteOrphanedDiskFiles,
        this.cleanupDeleteMissingMediaItems,
        (event: MaintenanceProgressEvent) => {
          this.cleanupProgress = event.processed;
          this.cleanupTotal = event.total;
          this.cleanupMessage = event.message || this.i18n.tr('maintenance.cleanup.running');
          if (event.done) {
            finalMessage = this.i18n.tr('maintenance.cleanup.finished', {
              deleted: event.deletedCount,
              updated: event.updatedCount,
              skipped: event.skippedCount,
              errors: event.errorCount,
            });
          }
        },
      );
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.cleanupRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.cleanup.title'));
    }
    await this.refreshInfo();
  }

  public async runGeoExtraction(): Promise<void> {
    if (this.geoExtractionRunning) return;

    this.geoExtractionRunning = true;
    this.geoExtractionProgress = 0;
    this.geoExtractionTotal = 0;
    this.geoExtractionMessage = '';
    let finalMessage = '';

    try {
      await streamExtractGeoLocations((event: MaintenanceProgressEvent) => {
        this.geoExtractionProgress = event.processed;
        this.geoExtractionTotal = event.total;
        this.geoExtractionMessage = event.message || this.i18n.tr('maintenance.geoExtraction.running');
        if (event.done) {
          finalMessage = this.i18n.tr('maintenance.geoExtraction.finished', {
            found: event.updatedCount,
            skipped: event.skippedCount,
            errors: event.errorCount,
          });
        }
      });
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.geoExtractionRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.geoExtraction.title'));
    }
    await this.refreshInfo();
  }

  public async runEmbeddedMetadataBackfill(): Promise<void> {
    if (this.embeddedMetadataBackfillRunning) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('maintenance.embeddedMetadataBackfill.confirm'),
      this.i18n.tr('maintenance.embeddedMetadataBackfill.title'),
    );
    if (!confirmed) return;

    this.embeddedMetadataBackfillRunning = true;
    this.embeddedMetadataBackfillProgress = 0;
    this.embeddedMetadataBackfillTotal = 0;
    this.embeddedMetadataBackfillMessage = '';
    let finalMessage = '';

    try {
      await streamBackfillEmbeddedMetadata((event: MaintenanceProgressEvent) => {
        this.embeddedMetadataBackfillProgress = event.processed;
        this.embeddedMetadataBackfillTotal = event.total;
        this.embeddedMetadataBackfillMessage = event.message
          || this.i18n.tr('maintenance.embeddedMetadataBackfill.running');
        if (event.done) {
          finalMessage = this.i18n.tr('maintenance.embeddedMetadataBackfill.finished', {
            found: event.updatedCount,
            skipped: event.skippedCount,
            errors: event.errorCount,
          });
        }
      });
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.embeddedMetadataBackfillRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(
        finalMessage,
        this.i18n.tr('maintenance.embeddedMetadataBackfill.title'),
      );
    }
    await this.refreshInfo();
  }

  public async runFaceEmbeddingBackfill(): Promise<void> {
    if (this.faceEmbeddingBackfillRunning) return;

    this.faceEmbeddingBackfillRunning = true;
    this.faceEmbeddingBackfillProgress = 0;
    this.faceEmbeddingBackfillTotal = 0;
    this.faceEmbeddingBackfillMessage = '';
    let finalMessage = '';

    try {
      await streamBackfillFaceEmbeddings((event: MaintenanceProgressEvent) => {
        this.faceEmbeddingBackfillProgress = event.processed;
        this.faceEmbeddingBackfillTotal = event.total;
        this.faceEmbeddingBackfillMessage = event.message || this.i18n.tr('maintenance.faceEmbeddingBackfill.running');
        if (event.done) {
          finalMessage = this.i18n.tr('maintenance.faceEmbeddingBackfill.finished', {
            found: event.updatedCount,
            skipped: event.skippedCount,
            errors: event.errorCount,
          });
        }
      });
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.faceEmbeddingBackfillRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.faceEmbeddingBackfill.title'));
    }
    await this.refreshInfo();
  }

  public async runFaceSharpnessBackfill(recalculateExisting = false): Promise<void> {
    if (this.faceSharpnessBackfillRunning) return;

    this.faceSharpnessBackfillRunning = true;
    this.faceSharpnessBackfillProgress = 0;
    this.faceSharpnessBackfillTotal = 0;
    this.faceSharpnessBackfillMessage = '';
    let finalMessage = '';

    try {
      await streamBackfillFaceSharpness((event: MaintenanceProgressEvent) => {
        this.faceSharpnessBackfillProgress = event.processed;
        this.faceSharpnessBackfillTotal = event.total;
        this.faceSharpnessBackfillMessage = event.message || this.i18n.tr('maintenance.faceSharpnessBackfill.running');
        if (event.done) {
          finalMessage = this.i18n.tr('maintenance.faceSharpnessBackfill.finished', {
            found: event.updatedCount,
            skipped: event.skippedCount,
            errors: event.errorCount,
          });
        }
      }, recalculateExisting);
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.faceSharpnessBackfillRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.faceSharpnessBackfill.title'));
    }
    await this.refreshInfo();
  }

  public async runMediumPreviewBackfill(): Promise<void> {
    if (this.mediumPreviewBackfillRunning) return;

    this.mediumPreviewBackfillRunning = true;
    this.mediumPreviewBackfillProgress = 0;
    this.mediumPreviewBackfillTotal = 0;
    this.mediumPreviewBackfillMessage = '';
    let finalMessage = '';

    try {
      await streamBackfillMediumPreviews((event: MaintenanceProgressEvent) => {
        this.mediumPreviewBackfillProgress = event.processed;
        this.mediumPreviewBackfillTotal = event.total;
        this.mediumPreviewBackfillMessage = event.message || this.i18n.tr('maintenance.mediumPreviewBackfill.running');
        if (event.done) {
          finalMessage = this.i18n.tr('maintenance.mediumPreviewBackfill.finished', {
            found: event.updatedCount,
            skipped: event.skippedCount,
            errors: event.errorCount,
          });
        }
      });
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.mediumPreviewBackfillRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.mediumPreviewBackfill.title'));
    }
    await this.refreshInfo();
  }

  public async runPersonMarkerPreviewBackfill(): Promise<void> {
    if (this.personMarkerPreviewBackfillRunning) return;

    this.personMarkerPreviewBackfillRunning = true;
    this.personMarkerPreviewBackfillProgress = 0;
    this.personMarkerPreviewBackfillTotal = 0;
    this.personMarkerPreviewBackfillMessage = '';
    let finalMessage = '';

    try {
      await streamBackfillPersonMarkerPreviews((event: MaintenanceProgressEvent) => {
        this.personMarkerPreviewBackfillProgress = event.processed;
        this.personMarkerPreviewBackfillTotal = event.total;
        this.personMarkerPreviewBackfillMessage = event.message
          || this.i18n.tr('maintenance.personMarkerPreviewBackfill.running');
        if (event.done) {
          finalMessage = this.i18n.tr('maintenance.personMarkerPreviewBackfill.finished', {
            found: event.updatedCount,
            skipped: event.skippedCount,
            errors: event.errorCount,
          });
        }
      });
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.personMarkerPreviewBackfillRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(
        finalMessage,
        this.i18n.tr('maintenance.personMarkerPreviewBackfill.title'),
      );
    }
    await this.refreshInfo();
  }

  public async runFilenameTitleCleanup(): Promise<void> {
    if (this.filenameTitlesRunning || !this.info || this.info.filenameTitleCount === 0) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('maintenance.filenameTitles.confirm', { count: this.info.filenameTitleCount }),
      this.i18n.tr('maintenance.filenameTitles.title'),
    );
    if (!confirmed) return;

    this.filenameTitlesRunning = true;
    this.filenameTitlesProgress = 0;
    this.filenameTitlesTotal = 0;
    this.filenameTitlesMessage = '';
    let finalMessage = '';

    try {
      await streamClearFilenameTitles((event: MaintenanceProgressEvent) => {
        this.filenameTitlesProgress = event.processed;
        this.filenameTitlesTotal = event.total;
        this.filenameTitlesMessage = event.message || this.i18n.tr('maintenance.filenameTitles.running');
        if (event.done) {
          finalMessage = this.i18n.tr('maintenance.filenameTitles.finished', {
            cleared: event.updatedCount,
            errors: event.errorCount,
          });
        }
      });
    } catch (error) {
      finalMessage = error instanceof Error ? error.message : String(error);
    } finally {
      this.filenameTitlesRunning = false;
    }

    if (finalMessage) {
      await ConfirmDialog.alert(finalMessage, this.i18n.tr('maintenance.filenameTitles.title'));
    }
    await this.refreshInfo();
  }
}
